import { Dialog, DialogWindow } from '../../../../widgets/dialog';
import { SearchInput } from '../../../../widgets/input';
import React, { FC, Suspense, useState } from 'react';
import { graphql, useLazyLoadQuery, usePaginationFragment } from 'react-relay';
import { CatalogListContent } from 'src/components/widgets/catalogList';
import { CatalogListItemModel } from 'src/components/widgets/catalogList/catalogListItemModel';
import Loader from 'src/components/widgets/loader';
import { T, toLocalID } from 'src/shared/helpers';
import styled from 'styled-components';
import { assetLibraryQueriesAIAssetLibraryCategoriesQuery } from '../../assetLibrary/__generated__/assetLibraryQueriesAIAssetLibraryCategoriesQuery.graphql';
import { assetPickerDialogAssets_query$key } from './__generated__/assetPickerDialogAssets_query.graphql';
import { assetPickerDialogAssetsRootQuery } from './__generated__/assetPickerDialogAssetsRootQuery.graphql';
import { assetPickerDialogAssetsPaginationQuery } from './__generated__/assetPickerDialogAssetsPaginationQuery.graphql';
import { aiAssetLibraryCategoriesQuery } from '../../assetLibrary/assetLibraryQueries';

const DialogDecorator = styled(DialogWindow)`
    width: 90%;
    height: 100%;
    padding-top: 20px;
    flex-basis: unset;
`;

const Container = styled.div`
    width: 100%;
    height: 100%;
    margin: auto;
    display: flex;
    flex-direction: column;
    overflow: hidden;
`;

const Title = styled.h3`
    text-align: left;
    margin-bottom: 16px;
    font-size: 18px;
`;

const Subtitle = styled.p`
    font-size: 14px;
    color: #6b7280;
    margin-bottom: 16px;
`;

const SearchContainer = styled.div`
    width: 100%;

    input {
        width: calc(100% - 4rem);
    }
`;

const FilterRow = styled.div`
    display: flex;
    gap: 8px;
    margin-top: 12px;
    flex-wrap: wrap;
`;

const CategoryChip = styled.button<{ $selected: boolean }>`
    padding: 6px 14px;
    border-radius: 16px;
    font-size: 13px;
    cursor: pointer;
    border: 1px solid ${(props) => (props.$selected ? props.theme.app.primary : props.theme.app.borderColor)};
    background: ${(props) => (props.$selected ? props.theme.app.primary : 'transparent')};
    color: ${(props) => (props.$selected ? '#fff' : props.theme.app.textColor)};
    transition: all 0.2s;

    &:hover {
        border-color: ${(props) => props.theme.app.primary};
    }
`;

const CatalogContainer = styled.div`
    margin-top: 20px;
    flex: 1;
    overflow: auto;
`;

const assetPickerAssetsFragment = graphql`
    fragment assetPickerDialogAssets_query on Query
    @refetchable(queryName: "assetPickerDialogAssetsPaginationQuery")
    @argumentDefinitions(
        count: { type: "Int", defaultValue: 50 }
        cursor: { type: "String" }
        categoryID: { type: "Int" }
        assetType: { type: "String" }
        sortBy: { type: "AIAssetLibrarySortBy" }
        sortDirection: { type: "SortDirection" }
    ) {
        aiAssetLibraryAssets(first: $count, after: $cursor, categoryID: $categoryID, assetType: $assetType, sortBy: $sortBy, sortDirection: $sortDirection)
            @connection(key: "assetPickerDialog_aiAssetLibraryAssets") {
            edges {
                node {
                    id
                    name
                    fileUrl
                    thumbnailUrl
                    categoryName
                }
            }
        }
    }
`;

interface AssetItemModel extends CatalogListItemModel {
    fileUrl: string;
}

interface AssetPickerContentProps {
    search: string;
    onSelect: (imageUrl: string) => void;
}

const assetPickerRootQuery = graphql`
    query assetPickerDialogAssetsRootQuery($categoryID: Int, $assetType: String, $sortBy: AIAssetLibrarySortBy, $sortDirection: SortDirection) {
        ...assetPickerDialogAssets_query @arguments(categoryID: $categoryID, assetType: $assetType, sortBy: $sortBy, sortDirection: $sortDirection)
    }
`;

const AssetPickerContent: FC<AssetPickerContentProps> = ({ search, onSelect }) => {
    const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);

    const categoriesData = useLazyLoadQuery<assetLibraryQueriesAIAssetLibraryCategoriesQuery>(
        aiAssetLibraryCategoriesQuery,
        {
            sortBy: 'CREATED_DATE',
            sortDirection: 'DESC'
        },
        { fetchPolicy: 'store-or-network' }
    );

    const assetsData = useLazyLoadQuery<assetPickerDialogAssetsRootQuery>(
        assetPickerRootQuery,
        {
            categoryID: selectedCategoryId ? toLocalID(selectedCategoryId) : undefined,
            assetType: 'image',
            sortBy: 'CREATED_DATE',
            sortDirection: 'DESC'
        },
        { fetchPolicy: 'store-or-network' }
    );

    const { data: assetsFragmentData } = usePaginationFragment<assetPickerDialogAssetsPaginationQuery, assetPickerDialogAssets_query$key>(
        assetPickerAssetsFragment,
        assetsData
    );

    const categories = categoriesData.aiAssetLibraryCategories?.nodes || [];
    const assets = (assetsFragmentData.aiAssetLibraryAssets?.edges || []).map((edge) => edge.node);

    // Filter by search text (client-side since the query doesn't support search)
    const filteredAssets = search ? assets.filter((a) => a.name.toLowerCase().includes(search.toLowerCase())) : assets;

    // Map to CatalogListItemModel format
    const catalogModels: AssetItemModel[] = filteredAssets.map((a) => ({
        id: a.id,
        title: a.name,
        subtitle: a.categoryName || '',
        thumbnail: a.thumbnailUrl || a.fileUrl || null,
        selected: false,
        fileUrl: a.fileUrl
    }));

    const handleItemClick = (model: AssetItemModel) => {
        onSelect(model.fileUrl);
    };

    return (
        <>
            {categories.length > 0 && (
                <FilterRow>
                    <CategoryChip $selected={selectedCategoryId === null} onClick={() => setSelectedCategoryId(null)}>
                        {T._('All', 'Admin')}
                    </CategoryChip>
                    {categories.map((category) => (
                        <CategoryChip key={category.id} $selected={selectedCategoryId === category.id} onClick={() => setSelectedCategoryId(category.id)}>
                            {category.name}
                        </CategoryChip>
                    ))}
                </FilterRow>
            )}
            <CatalogContainer>
                <CatalogListContent<AssetItemModel>
                    models={catalogModels}
                    loadNext={() => {}}
                    hasNext={false}
                    isLoadingNext={false}
                    onItemClick={handleItemClick}
                    noItemsMessage={T._('No assets found', 'Admin')}
                    noItemsSubMessage={
                        search
                            ? T._('No assets match your search. Try different keywords.', 'Admin')
                            : T._('Save outputs from AI Studio tools to build your library.', 'Admin')
                    }
                    hideAlreadyConfiguredBadge={true}
                    columns={{
                        xs: 5,
                        md: 7,
                        lg: 7
                    }}
                />
            </CatalogContainer>
        </>
    );
};

interface AssetPickerDialogProps {
    onSelect: (imageUrl: string) => void;
}

export const AssetPickerDialog: FC<AssetPickerDialogProps> = ({ onSelect }) => {
    const [search, setSearch] = useState('');

    return (
        <Dialog windowDecorator={DialogDecorator}>
            <Container>
                <Title>{T._('Select from library', 'Admin')}</Title>
                <Subtitle>{T._('Choose an asset to use as input', 'Admin')}</Subtitle>
                <SearchContainer>
                    <SearchInput placeholder={T._('Search assets by name...', 'Admin')} onSearch={(criteria) => setSearch(criteria)} />
                </SearchContainer>

                <Suspense fallback={<Loader />}>
                    <AssetPickerContent search={search} onSelect={onSelect} />
                </Suspense>
            </Container>
        </Dialog>
    );
};
